import {
	Button,
	Checkbox,
	FormControl,
	FormErrorMessage,
	FormHelperText,
	FormLabel,
	Heading,
	Input,
	InputGroup,
	InputLeftAddon,
	NumberDecrementStepper,
	NumberIncrementStepper,
	NumberInput,
	NumberInputField,
	NumberInputStepper,
	Textarea,
} from "@chakra-ui/react";
import { Trash2 } from "lucide-react";
import { PropsWithChildren } from "react";
import { useMutation } from "@apollo/client";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { format, isBefore } from "date-fns";
import { PortfolioWorkHistoryItem } from "../../types";
import {
	EDIT_PROJECT_HISTORY,
	REMOVE_PROJECT_HISTORY,
} from "src/common/graphql/typed-mutations";
import { FaUpload } from "react-icons/fa";
import React from "react";
import { useToast } from "@chakra-ui/react";
interface WorkHistoryEditorProps {
	onClose: () => void;
	item: PortfolioWorkHistoryItem & { id: string };
}

const formSchema = z
	.object({
		taskName: z.string().min(3),
		taskDescription: z.string().min(10),
		budget: z.number().optional(),
		proDoesNotWishToDiscloseBudget: z.boolean().optional(),
		date: z.string().refine((value) => {
			if (!value) return false;
			return isBefore(new Date(value), new Date());
		}, "Date must be today or in the past"),
		images: z
			.array(
				z.object({
					name: z.string(),
					data: z.string(),
					mimeType: z.string(),
				})
			)
			.optional(),
	})
	.refine(
		(data) => {
			if (!data.budget && !data.proDoesNotWishToDiscloseBudget) {
				return false;
			}

			return true;
		},
		{
			message: "A budget is required unless you do not wish to disclose it",
			path: ["budget"],
		}
	);

export const WorkHistoryEditor = ({
	onClose,
	item,
}: PropsWithChildren<WorkHistoryEditorProps>) => {
	const toast = useToast();

	const [existingImagesToKeep, setExistingImagesToKeep] = React.useState<
		string[]
	>([]);
	const [selectedPortfolioImages, setSelectedPortfolioImages] = React.useState<
		string[]
	>([]);

	React.useEffect(() => {
		setExistingImagesToKeep(item.images?.map((img) => img.id) || []);
	}, [item.images]);

	const {
		register,
		watch,
		setValue,
		handleSubmit,
		formState: { errors },
	} = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			taskName: item.name,
			taskDescription: item.description,
			...(item.budget && { budget: item.budget }),
			...(item.proDoesNotWishToDiscloseBudget && {
				proDoesNotWishToDiscloseBudget: item.proDoesNotWishToDiscloseBudget,
			}),
			date: format(new Date(item.endedAt), "yyyy-MM-dd"),
		},
	});

	const [editProjectHistory, editProjectHistoryHandle] = useMutation(
		EDIT_PROJECT_HISTORY,
		{
			onCompleted: () => {
				toast({
					title: "Project history item updated",
					status: "success",
					duration: 3000,
					isClosable: true,
				});
				onClose();
			},
			refetchQueries: ["MyPortfolioWithMedia"],
			awaitRefetchQueries: true,
		}
	);

	const [removeProjectHistory, removeProjectHistoryHandle] = useMutation(
		REMOVE_PROJECT_HISTORY,
		{
			onCompleted: () => {
				onClose();
			},
			refetchQueries: ["MyPortfolioWithMedia"],
			awaitRefetchQueries: true,
		}
	);

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!e.target.files) return;
		const files = Array.from(e.target.files);

		files.forEach((file) => {
			const reader = new FileReader();
			reader.onloadend = () => {
				if (!reader.result) return;

				if (typeof reader.result === "string") {
					const image = reader.result;

					setSelectedPortfolioImages((prev) => [...prev, image]);

					const dataObject = {
						name:
							"job-history-image-" +
							reader.result.split(",")[1].substring(0, 5) +
							`-${Math.random() * 1000}`,
						data: reader.result.split(",")[1],
						mimeType: file.type,
					};

					// Store in form state for submission
					const currentImages = (watch("images") as any[]) || [];
					setValue("images", [...currentImages, dataObject]);
				}
			};

			reader.readAsDataURL(file);
		});
	};

	const onSubmit = (data: z.infer<typeof formSchema>) => {
		const images = data.images || [];
		editProjectHistory({
			variables: {
				id: item.id,
				name: data.taskName,
				description: data.taskDescription,
				...(data.budget && data.budget > 0 && { budget: data.budget }),
				proDoesNotWishToDiscloseBudget: !!data.proDoesNotWishToDiscloseBudget,
				existingImageIds: existingImagesToKeep,
				images: images.length > 0 ? images : undefined,
				...(data.date && { startedAt: new Date(data.date) }),
				endedAt: null,
			},
		});
	};

	const handleRemove = () => {
		removeProjectHistory({
			variables: {
				id: item.id,
			},
		});
	};

	const budget = watch("budget");
	const proDoesNotWishToDiscloseBudget = watch(
		"proDoesNotWishToDiscloseBudget"
	);

	return (
		<div className="flex flex-col py-4 gap-4 w-full max-h-[80vh] overflow-y-auto">
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="flex flex-col gap-8">
					<div className="flex flex-col gap-4">
						<FormControl isInvalid={!!errors.taskName} isRequired>
							<FormLabel htmlFor="firstName">Project Name</FormLabel>
							<Input
								id="taskName"
								placeholder="Example: 2HP Split AC Installation"
								{...register("taskName")}
								variant="outline"
							/>
							<FormErrorMessage>{errors.taskName?.message}</FormErrorMessage>
						</FormControl>
						<FormControl isInvalid={!!errors.taskDescription} isRequired>
							<FormLabel htmlFor="taskDescription">
								Project Description
							</FormLabel>
							<Textarea
								id="taskDescription"
								placeholder="Example: I installed a 2HP split AC in the second floor living room."
								{...register("taskDescription")}
							/>
							<FormErrorMessage>
								{errors.taskDescription?.message}
							</FormErrorMessage>
						</FormControl>

						<FormControl isInvalid={!!errors.date} isRequired>
							<FormLabel htmlFor="taskDate">Completion Date</FormLabel>
							<Input
								id="taskDate"
								{...register("date")}
								type="date"
								variant="outline"
							/>
							<FormErrorMessage>{errors.date?.message}</FormErrorMessage>
						</FormControl>
						<FormControl isInvalid={!!errors.budget}>
							<FormLabel htmlFor="budget">Project Budget</FormLabel>
							<div className="flex flex-col gap-2">
								<FormControl
									isInvalid={!!errors.proDoesNotWishToDiscloseBudget}
									className="mt-2"
								>
									<div className="flex items-center gap-1">
										<Checkbox
											id="proDoesNotWishToDiscloseBudget"
											colorScheme="brand"
											{...register("proDoesNotWishToDiscloseBudget")}
											isDisabled={!!budget}
										/>
										<FormLabel
											fontSize={"14px"}
											color={"gray.500"}
											margin={0}
											fontWeight={"normal"}
											htmlFor="proDoesNotWishToDiscloseBudget"
										>
											I do not wish to disclose the budget
										</FormLabel>
									</div>
								</FormControl>
								<InputGroup>
									<InputLeftAddon>₦</InputLeftAddon>
									<NumberInput
										className="w-full"
										onChange={(_, value) => {
											if (isNaN(value)) {
												setValue("budget", 0);
												return;
											}
											setValue("budget", value);
										}}
										value={watch("budget")}
										step={1000}
										isDisabled={!!proDoesNotWishToDiscloseBudget}
									>
										<NumberInputField roundedLeft={0} />
										<NumberInputStepper>
											<NumberIncrementStepper />
											<NumberDecrementStepper />
										</NumberInputStepper>
									</NumberInput>
								</InputGroup>
							</div>
							<FormHelperText fontSize={"14px"} color={"gray.500"}>
								This is not displayed publicly
							</FormHelperText>
							<FormErrorMessage>{errors.budget?.message}</FormErrorMessage>
						</FormControl>
					</div>

					<div className="flex flex-col items-start gap-2">
						<div className="flex flex-col gap-1">
							<Heading as="h4" className="!text-xl !font-medium">
								Project Photos
							</Heading>
							<p className="text-sm text-gray-600">
								Photos are optional, but they are what populate your LaborPass
								gallery and help clients see the project you completed.
							</p>
						</div>

						{/* Display existing images */}
						{item.images &&
							item.images.filter((img) => existingImagesToKeep.includes(img.id))
								.length > 0 && (
								<div className="flex flex-wrap gap-4 w-full">
									{item.images
										.filter((img) => existingImagesToKeep.includes(img.id))
										.map((image) => (
											<div key={image.id} className="relative">
												<img
													src={image.thumbnailUrl || undefined}
													alt={image.name}
													className="max-h-[250px] max-w-[300px] rounded border"
												/>
												<Button
													position="absolute"
													top={2}
													right={2}
													size="sm"
													colorScheme="red"
													onClick={() => {
														setExistingImagesToKeep((prev) =>
															prev.filter((id) => id !== image.id)
														);
													}}
												>
													Remove
												</Button>
											</div>
										))}
								</div>
							)}

						{/* Display new images being uploaded */}
						{selectedPortfolioImages.length > 0 && (
							<div className="flex flex-wrap gap-4 w-full">
								{selectedPortfolioImages.map((image, index) => (
									<div key={index} className="relative">
										<img
											src={image}
											alt={`Upload Preview ${index + 1}`}
											className="max-h-[250px] max-w-[300px] rounded border"
										/>
										<Button
											position="absolute"
											top={2}
											right={2}
											size="sm"
											colorScheme="red"
											onClick={() => {
												setSelectedPortfolioImages((prev) =>
													prev.filter((_, i) => i !== index)
												);
												// Also remove from form state
												const currentImages = (watch("images") as any[]) || [];
												setValue(
													"images",
													currentImages.filter((_, i) => i !== index)
												);
											}}
										>
											Remove
										</Button>
									</div>
								))}
							</div>
						)}

						<div className="relative flex gap-4">
							<input
								type="file"
								accept="image/*"
								onChange={handleFileChange}
								id="upload-btn"
								hidden
								multiple
							/>
							<Button
								onClick={() => {
									document.getElementById("upload-btn")?.click();
								}}
								leftIcon={<FaUpload />}
								colorScheme="prussianBlue"
								variant="outline"
							>
								{selectedPortfolioImages.length
									? "Add More Photos"
									: "Choose Photos"}
							</Button>
						</div>
					</div>

					<div className="flex justify-between">
						<Button
							colorScheme="red"
							variant="outline"
							leftIcon={<Trash2 className="w-5 h-5" />}
							isLoading={removeProjectHistoryHandle.loading}
							onClick={handleRemove}
						>
							Delete
						</Button>
						<div>
							<Button variant="ghost" mr={3} onClick={onClose}>
								Close
							</Button>
							<Button
								colorScheme="brand"
								isLoading={editProjectHistoryHandle.loading}
								type="submit"
							>
								Save
							</Button>
						</div>
					</div>
				</div>
			</form>
		</div>
	);
};
